import React, { useState } from 'react'
import { ReportHeading } from '../../Reports/common/ReportHeading'
import { Item } from '../../../common/snippets'
import { Box, Button } from '@mui/material'
import DropDownOA from '../../../common/dropDownOA'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import useToken from '../../../helpers/useTokens'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'
import Datepicker from '../../../common/datePicker'
import { SplitButton } from '../../../common/SplitButton'
import { openReportInNewTab } from '../../Reports/DispatchReport/DispatchReportUtils'
import { urls, xlCol, xlColq } from '../../../common/constants'
import { useSetAtom } from 'jotai'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import { GetRequest } from '../../../common/api'
import { downloadMultiTablePDF } from '../../Reports/analysisReport/pdfAutotable'

const reportName = 'Black Book Report'

const createColumn = (field, header, width, dataType = 'string', align = 'left') => ({
    field,
    header,
    exportHead: header,
    dataKey: field,
    align,
    width,
    weight: Number.parseInt(width, 10) || 1,
    xlCol: { ...xlCol, dataType, dataFormatter: null },
    headerSx: { minWidth: width, textAlign: align },
})

const stageSummaryColumns = [
    createColumn('label1', '', '170px'),
    createColumn('value1', '', '90px', 'number', 'right'),
    createColumn('label2', '', '170px'),
    createColumn('value2', '', '90px', 'number', 'right'),
    createColumn('label3', '', '170px'),
    createColumn('value3', '', '90px', 'number', 'right'),
    createColumn('label4', '', '170px'),
    createColumn('value4', '', '90px', 'number', 'right'),
    createColumn('label5', '', '170px'),
    createColumn('value5', '', '90px', 'number', 'right'),
]

const summaryStages = [
    ['Booked', 'BookedUnits'],
    ['Branch Transfer', 'BranchTransferUnits'],
    ['Sorting Transfer Receive', 'BranchTransferReceiveUnits'],
    ['Dispatch', 'DispatchUnits'],
    ['Destination Receive', 'ReceivedUnits'],
    ['Allocation (Assigned)', 'AllocatedUnits'],
    ['Out Delivered / Delivered', 'OutDeliveredUnits'],
    ['Returned', 'ReturnedUnits'],
    ['Refund', 'RefundUnits'],
]

const summaryLabelCellStyle = {
    backgroundColor: '#f1f9ff',
}

const buildSummaryEntryRow = (items = []) => items.reduce((row, [label, value], index) => ({
    ...row,
    [`label${index + 1}`]: label,
    [`value${index + 1}`]: value,
    cellStyle: {
        ...row.cellStyle,
        [`label${index + 1}`]: summaryLabelCellStyle,
    },
}), { dataType: 'entry' })

const buildSummaryTable = (summaryRow = {}) => ([
    {
        label1: 'Parcel Count By Event Stage (Transaction Date Wise)',
        dataType: 'header',
        colSpan: { label1: stageSummaryColumns.length },
        align: { label1: 'left' },
    },
    buildSummaryEntryRow(summaryStages.slice(0, 5).map(([label, key]) => [label, summaryRow?.[key] ?? ''])),
    buildSummaryEntryRow([...summaryStages.slice(5).map(([label, key]) => [label, summaryRow?.[key] ?? '']), ['', '']]),
])

const BlackBookReport = () => {
    const [filters, setFilters] = useState({
        fromDate: moment(new Date()),
        toDate: moment(new Date()),
    })
    const setAlert = useSetAtom(alertAtom)
    const setLoader = useSetAtom(globalLoaderAtom)
    const [searchParams] = useSearchParams()
    let { loginData = {} } = useToken()
    let isFromUrl = false

    if (!loginData) {
        loginData = {}
        loginData.intCompanyID = Number(searchParams.get('C')) || 0
        loginData.intUserID = Number(searchParams.get('U')) || 0
        loginData.intBranchID = Number(searchParams.get('B')) || 0
        loginData.intBranchCityID = Number(searchParams.get('CT')) || 0
        loginData.strCityName = searchParams.get('CTN') || ''
        loginData.strCompanyName = searchParams.get('CN') || ''
        loginData.strUserName = searchParams.get('UN') || ''
        loginData.strBranchName = searchParams.get('BN') || ''
        isFromUrl = true
    }

    const filterItemSx = isFromUrl ? sx.itemsxGrow : sx.itemsx
    const criteriaHeaders = [
        { field: 'fromDate', header: 'From Date', textAlign: 'left' },
        { field: 'toDate', header: 'To Date', textAlign: 'left' },
    ]
    const criteriaData = {
        fromDate: moment(filters?.fromDate).format('DD-MM-YYYY'),
        toDate: moment(filters?.toDate).format('DD-MM-YYYY'),
        dataType: 'entry',
    }
    const Table1ColDefs = [
        { header: 'SN', exportHead: 'SN', field: 'SN', weight: 4, xlCol: xlColq, headerSx: { width: '4%', textAlign: 'center' } },
        { header: 'Waybill No', exportHead: 'Waybill No', field: 'LRno', weight: 10, xlCol: xlCol, headerSx: { width: '10%', textAlign: 'left' } },
        { header: 'Last Event', exportHead: 'Last Event', field: 'LastEvent', weight: 12, xlCol: xlCol, headerSx: { width: '12%', textAlign: 'left' } },
        { header: 'Last Event Date-Time', exportHead: 'Last Event Date-Time', field: 'LastEventDateTime', weight: 12, xlCol: xlCol, headerSx: { width: '12%', textAlign: 'left' } },
        { header: 'Expected Next Event', exportHead: 'Expected Next Event', field: 'ExpectedNextEvent', weight: 13, xlCol: xlCol, headerSx: { width: '13%', textAlign: 'left' } },
        { header: 'Days Stuck', exportHead: 'Days Stuck', field: 'DaysStuck', weight: 7, xlCol: xlColq, headerSx: { width: '7%', textAlign: 'center' } },
        { header: 'Risk Level', exportHead: 'Risk Level', field: 'RiskLevel', weight: 8, xlCol: xlCol, headerSx: { width: '8%', textAlign: 'left' } },
        { header: 'Responsible Branch', exportHead: 'Responsible Branch', field: 'CurrentBranch', weight: 12, xlCol: xlCol, headerSx: { width: '12%', textAlign: 'left' } },
        { header: 'Issue Type', exportHead: 'Issue Type', field: 'IssueType', weight: 10, xlCol: xlCol, headerSx: { width: '10%', textAlign: 'left' } },
        { header: 'Action Required', exportHead: 'Action Required', field: 'ActionRequired', weight: 12, xlCol: xlCol, headerSx: { width: '12%', textAlign: 'left' } }
    ]
    const Table2ColDefs = [
        { header: 'SN', exportHead: 'SN', field: 'SN', weight: 4, xlCol: xlColq, headerSx: { width: '4%', textAlign: 'center' } },
        { header: 'Waybill No', exportHead: 'Waybill No', field: 'LRNO', weight: 10, xlCol: xlCol, headerSx: { width: '10%', textAlign: 'left' } },
        { header: 'Source - Destination', exportHead: 'Source - Destination', field: 'SourceandDestCity', weight: 16, xlCol: xlCol, headerSx: { width: '16%', textAlign: 'left' } },
        { header: 'Refund Date-Time', exportHead: 'Refund Date-Time', field: 'RefundDate', weight: 14, xlCol: xlCol, headerSx: { width: '14%', textAlign: 'left' } },
        { header: 'Refund Amount', exportHead: 'Refund Amount', field: 'RefundAmount', weight: 10, xlCol: xlColq, headerSx: { width: '10%', textAlign: 'right' } },
        { header: 'Status', exportHead: 'Status', field: 'Status', weight: 8, xlCol: xlCol, headerSx: { width: '8%', textAlign: 'left' } },
        { header: 'Refund By Branch', exportHead: 'Refund By Branch', field: 'RefundByBranch', weight: 14, xlCol: xlCol, headerSx: { width: '14%', textAlign: 'left' } },
        { header: 'Refund By User', exportHead: 'Refund By User', field: 'RefundByUser', weight: 10, xlCol: xlCol, headerSx: { width: '10%', textAlign: 'left' } },
        { header: 'Refund Reason', exportHead: 'Refund Reason', field: 'RefundReason', weight: 14, xlCol: xlCol, headerSx: { width: '14%', textAlign: 'left' } }
    ]
    const buildReportPayload = (reportData) => ({
        reportName,
        isMultiTable: true,
        hideHeader: true,
        showColumnBorders: true,
        isWrapEntryText: true,
        tables: [
            {
                criteriaHeaders: criteriaHeaders,
                criteriaData: [criteriaData],
                mainHeader: stageSummaryColumns,
                tableData: reportData?.Table,
            },
            {
                mainHeader: Table1ColDefs,
                tableData: reportData?.Table1,
            },
            {
                mainHeader: Table2ColDefs,
                tableData: reportData?.Table2,
            },

        ],
    })
    const getBlackBookReportData = async (obj) => {
        try {
            const res = await GetRequest(urls.getBlackBookReportData, obj)

            if (res.status !== 'Success') {
                setAlert({ show: true, msg: res?.ErrMessage, nextId: '' })
                return
            }

            const tables = res?.Data || {}
            tables.Table = buildSummaryTable(tables?.Table?.[0] || {})
            tables.Table1 = tables?.Table1?.map((val, i) => ({
                ...val,
                SN: i + 1,
                dataType: 'entry'
            }))
            tables.Table2 = tables?.Table2?.map((val, i) => ({
                ...val,
                SN: i + 1,
                dataType: 'entry',
                Status: 'Approved'
            }))
            const headerCol = Table1ColDefs.reduce((a, c) => ((a[c.field] = c.header), a), {})
            tables?.Table1?.unshift({ dataType: 'header', ...headerCol },)
            tables?.Table1?.unshift({ dataType: 'subHeader', SN: 'High Risk - Missing Event Parcels (Transaction Date Wise)', colSpan: { SN: Table1ColDefs?.length }, align: { SN: 'left' } })
            const headerCol2 = Table2ColDefs.reduce((a, c) => ((a[c.field] = c.header), a), {})
            tables?.Table2?.unshift({ dataType: 'header', ...headerCol2 },)
            tables?.Table2?.unshift({ dataType: 'subHeader', SN: 'Refund Overview (Refund Date Wise)', colSpan: { SN: Table2ColDefs?.length }, align: { SN: 'left' } })

            return tables
        } catch (error) {
            setAlert({ show: true, msg: error?.message, nextId: '' })
        }
    }

    const handleClick1 = async (isPdf = false) => {
        try {
            const body = {
                CompanyID: loginData?.intCompanyID,
                FromDate: moment(new Date(filters?.fromDate)).format('YYYY-MM-DD'),
                ToDate: moment(new Date(filters?.toDate)).format('YYYY-MM-DD')
            }

            setLoader(true)

            const reportData = await getBlackBookReportData(body)

            setLoader(false)
            if (isPdf === true) {
                downloadMultiTablePDF({
                    reportName,
                    criteriaHeaders,
                    criteriaData: [criteriaData],
                    isMultiTable: true,
                    hideHeader: true,
                    showColumnBorders: true,
                    isWrapEntryText: true,
                    tables: [
                        {
                            visibleColumns: stageSummaryColumns,
                            tableData: reportData?.Table,
                            hideHeader: true,
                        },
                        {
                            visibleColumns: Table1ColDefs,
                            tableData: reportData?.Table1,
                            hideHeader: true,
                        },
                        {
                            visibleColumns: Table2ColDefs,
                            tableData: reportData?.Table2,
                            hideHeader: true,
                        },

                    ],
                    loginData,
                })
                return
            }

            openReportInNewTab(buildReportPayload(reportData), isPdf)

        } catch (err) {
            setLoader(false)
            setAlert({ show: true, msg: err.message, nextId: '' })
        }
    }

    return (
        <>
            <Item sx={{ padding: '0' }} className="reports">
                {isFromUrl && <style>{`.global-header { display: none; } #root { zoom: 1.2; }`}</style>}
                {isFromUrl ? <></> : <ReportHeading reportName={reportName} />}
                <Box sx={sx.reportFlex}>
                    {isFromUrl ? <></> : <>
                        <Item sx={sx.itemsx}>
                            <DropDownOA
                                id={'selectCompnay'}
                                label={'Select Company'}
                                defaultvalue={loginData?.strCompanyName}
                                disabled={true}
                                minWidth="250px"
                            />
                        </Item>
                        <Item sx={sx.itemsx} className="disabled-wrapper">
                            <Button
                                variant="contained"
                                disabled={true}
                                style={{ ...sx.fullHeight, ...sx.novp }}
                                id={'goButton'}
                                className="btn btn-primary"
                            >
                                GO
                            </Button>
                        </Item></>}
                        <Item sx={filterItemSx} className="input--container bookingDatePicker">
                            <Datepicker
                                name={'from_collection_date'}
                                value={filters?.fromDate}
                                label={'From Booking Date'}
                                id={'fromDate'}
                                dateChange={(date) => {
                                    setFilters((prev) => ({ ...prev, fromDate: date }))
                                }}
                                width={isFromUrl ? '100%' : '160px'}
                            />
                        </Item>
                        <Item sx={filterItemSx} className="input--container bookingDatePicker">
                            <Datepicker
                                label={'To Booking Date'}
                                value={filters?.toDate}
                                id={'toDate'}
                                dateChange={(date) => {
                                    setFilters((prev) => ({ ...prev, toDate: date }))
                                }}
                                width={isFromUrl ? '100%' : '160px'}
                            />
                        </Item>
                        {isFromUrl ?
                            <Button variant="contained" sx={{ width: '100%', height: '40px', marginBottom: '10px' }} className="btn btn-primary" onClick={() => handleClick1(true)} id1={'reportmainButton'}>
                                DOWNLOAD PDF
                            </Button> :
                            <Item sx={sx.itemsx}>
                                <SplitButton
                                    hideToggle={true}
                                    options={['GENERATE']}
                                    id1={'splitButton'}
                                    onClick1={() => handleClick1(false)}
                                />
                            </Item>
                        }
                    
                </Box>
            </Item>
        </>
    )
}

export default BlackBookReport
